/**
 * Geohash Utilities
 *
 * Used for room discovery by geographic area.
 */

import ngeohash from 'ngeohash'
import type { GeoLocation } from '@/lib/types'

// Geohash precision levels
// 1: ~5000km, 2: ~1250km, 3: ~156km, 4: ~39km
// 5: ~4.9km, 6: ~1.2km, 7: ~153m, 8: ~38m
const DISCOVERY_PRECISION = 6 // ~1.2km x 0.6km cells

/**
 * Encode a location to a geohash string
 */
export function encodeGeohash(
  location: GeoLocation,
  precision: number = DISCOVERY_PRECISION
): string {
  return ngeohash.encode(location.latitude, location.longitude, precision)
}

/**
 * Decode a geohash to a location
 */
export function decodeGeohash(hash: string): GeoLocation {
  const { latitude, longitude } = ngeohash.decode(hash)
  return { latitude, longitude }
}

/**
 * Get the bounding box for a geohash
 */
export function getGeohashBounds(hash: string): {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
} {
  const bounds = ngeohash.decode_bbox(hash)
  return {
    minLat: bounds[0],
    minLng: bounds[1],
    maxLat: bounds[2],
    maxLng: bounds[3],
  }
}

/**
 * Get neighboring geohashes (for room discovery across boundaries)
 */
export function getNeighborGeohashes(hash: string): string[] {
  return ngeohash.neighbors(hash)
}

/**
 * Get the geohash for room discovery (and neighbors for edge cases)
 */
export function getDiscoveryGeohashes(location: GeoLocation): string[] {
  const center = encodeGeohash(location, DISCOVERY_PRECISION)
  const neighbors = getNeighborGeohashes(center)
  return [center, ...neighbors]
}

/**
 * Check if a location is within a geohash area
 */
export function isInGeohash(location: GeoLocation, hash: string): boolean {
  const bounds = getGeohashBounds(hash)
  return (
    location.latitude >= bounds.minLat &&
    location.latitude <= bounds.maxLat &&
    location.longitude >= bounds.minLng &&
    location.longitude <= bounds.maxLng
  )
}
